/* ============================================================================
 * Brand video flags — shared types + colour system (admin side)
 * ============================================================================
 * A brand can flag a delivered video ("BMC, look at this one") and must say
 * why in its own words — the reason is free text, not a picked category, so
 * what ops reads is the brand's actual account of the problem. The flag lives
 * on the submission row itself: the brand_flag_* columns on
 * cr_video_submissions (see creator-app/supabase/migrations/
 * 20260903010000_cr_video_submissions_brand_flags.sql), so every surface that
 * already selects submissions gets the flag for free.
 *
 * A flag changes NOTHING about the money: payouts, refunds, opsBucket and the
 * Pay/Refund buttons are untouched by it. It's a warning to read, not a gate.
 * What it does demand is an answer:
 *
 *   Approve the video anyway → the admin must WRITE a reason, and the brand
 *     reads that text back on the video. Overriding a brand's objection is
 *     allowed; doing it silently is not.
 *   Reject the video         → the flag is upheld, and the reject reason is
 *     copied into the resolution so both sides carry the same words.
 *
 * Rendered in three places, all reading this one module so the tone can't
 * drift: the per-hire count in CreatorPayments, the Flag column in the hire
 * detail video table, and VideoFlagsCard above it.
 * ========================================================================== */

/** Flagging is one-way for the brand: they cannot withdraw a flag, so the only
 *  transitions are active → dismissed / upheld, and only an admin makes them. */
export type FlagStatus = 'active' | 'dismissed' | 'upheld';

/** The flag half of a cr_video_submissions row. */
export type VideoFlagFields = {
  brand_flag_status: FlagStatus | null;
  /** The brand's own words on what's wrong. Free text, required when raised. */
  brand_flag_reason: string | null;
  brand_flagged_by_email: string | null;
  brand_flagged_at: string | null;
  brand_flag_resolved_by_email: string | null;
  brand_flag_resolved_at: string | null;
  /** Why BMC approved a flagged video (or the reject reason when upheld).
   *  Shown to the brand verbatim. */
  brand_flag_resolution_notes: string | null;
  brand_flag_count: number | null;
};

/** Columns to add to a cr_video_submissions `select()` for any flag UI. */
export const VIDEO_FLAG_COLUMNS =
  'brand_flag_status, brand_flag_reason, brand_flagged_by_email, brand_flagged_at, ' +
  'brand_flag_resolved_by_email, brand_flag_resolved_at, brand_flag_resolution_notes, brand_flag_count';

export const MAX_FLAG_RESOLUTION_NOTES = 2000;
/** Long enough to be a real explanation — the brand reads this, so "ok" won't
 *  do. Mirrors the floor the brand's own flag reason has to clear. */
export const MIN_FLAG_RESOLUTION_NOTES = 10;

/** Tailwind tones per state. `active` is the only one that shouts; a resolved
 *  flag is history and should read like it. */
export const FLAG_STATUS_META: Record<FlagStatus, {
  label: string;
  /** Chip classes for the inline badge. */
  badge: string;
  /** Icon / heading colour. */
  icon: string;
  /** Card/callout surface — translucent, for panels sitting on the page's own
   *  opaque background (VideoFlagsCard). NOT for a floating tooltip: anything
   *  see-through there lets the table underneath bleed through the text. */
  surface: string;
  /** Border colour on its own, for a floating surface that has to keep the
   *  opaque `bg-popover` it comes with. */
  border: string;
  /** Row tint for the video table. */
  row: string;
}> = {
  active: {
    label: 'Flagged by brand',
    badge: 'bg-red-100 text-red-800 border-red-200',
    icon: 'text-red-600',
    surface: 'border-red-200 bg-red-50/60',
    border: 'border-red-200',
    row: 'bg-red-50/60',
  },
  dismissed: {
    label: 'Approved despite flag',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: 'text-emerald-600',
    surface: 'border-emerald-200 bg-emerald-50/50',
    border: 'border-emerald-200',
    row: '',
  },
  upheld: {
    label: 'Flag upheld',
    badge: 'bg-violet-100 text-violet-800 border-violet-200',
    icon: 'text-violet-600',
    surface: 'border-violet-200 bg-violet-50/50',
    border: 'border-violet-200',
    row: '',
  },
};

/** Still waiting on a BMC answer. */
export function isFlagActive(v: Partial<VideoFlagFields> | null | undefined): boolean {
  return v?.brand_flag_status === 'active';
}

/** Flagged at any point. A flag can't be removed, so this is simply "does this
 *  video have one" — the status says whether BMC has answered it yet. */
export function hasFlag(v: Partial<VideoFlagFields> | null | undefined): boolean {
  return v?.brand_flag_status != null;
}

/**
 * A one-line lead-in of a free-text reason, for a table cell or a chip. Cuts
 * at a word boundary where there's one nearby so it doesn't end mid-word; the
 * full text always lives in the hover card and the flag card.
 */
export function flagReasonPreview(reason: string | null | undefined, max = 60): string {
  const trimmed = (reason ?? '').trim();
  if (!trimmed) return 'No reason given';
  if (trimmed.length <= max) return trimmed;
  const cut = trimmed.slice(0, max);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + '…';
}

/** Per-hire tallies from that hire's submissions — what the listing badge and
 *  the hire detail card both count. */
export function flagCounts(videos: Array<Partial<VideoFlagFields>> | null | undefined): {
  active: number; resolved: number; total: number;
} {
  let active = 0, resolved = 0;
  for (const v of videos ?? []) {
    if (isFlagActive(v)) active++;
    else if (hasFlag(v)) resolved++;
  }
  return { active, resolved, total: active + resolved };
}
